<template>
    <modal
        ref="modal"
        :title="modalTitle"
        class-name="customer-service-product-modal"
        @close="handleClose"
    >
        <form
            ref="form"
            @submit.prevent="save"
        >
            <div class="form-group">
                <label for="service_product_id">Service Product</label>
                <select
                    v-if="!form.editingId"
                    id="service_product_id"
                    v-model="form.service_product_id"
                    class="form-control"
                    :class="{ 'is-invalid': errors.service_product_id }"
                    @change="onProductChange"
                >
                    <option
                        :value="null"
                        disabled
                    >
                        Select service product
                    </option>
                    <option
                        v-for="sp in availableServiceProducts"
                        :key="sp.id"
                        :value="sp.id"
                    >
                        {{ sp.name }}
                    </option>
                </select>
                <input
                    v-else
                    type="text"
                    class="form-control"
                    :value="form.productDisplayName"
                    readonly
                    disabled
                >
                <error
                    :errors="errors"
                    name="service_product_id"
                />
            </div>
            <div class="row">
                <div class="col-sm-6">
                    <div class="form-group">
                        <label for="fitting_price">Fitting Price</label>
                        <div class="input-group">
                            <div class="input-group-prepend">
                                <span class="input-group-text">£</span>
                            </div>
                            <input
                                id="fitting_price"
                                v-model="form.fitting_price"
                                type="number"
                                step="0.01"
                                min="0"
                                class="form-control"
                                placeholder="Use default"
                                :class="{ 'is-invalid': errors.fitting_price }"
                            >
                        </div>
                        <small
                            v-if="originalFittingPrice != null"
                            class="form-text text-muted"
                        >
                            Original: £{{ originalFittingPrice | money }}
                        </small>
                        <error
                            :errors="errors"
                            name="fitting_price"
                        />
                    </div>
                </div>
                <div class="col-sm-6">
                    <div class="form-group">
                        <label for="id_sign_price">ID Sign Price</label>
                        <div class="input-group">
                            <div class="input-group-prepend">
                                <span class="input-group-text">£</span>
                            </div>
                            <input
                                id="id_sign_price"
                                v-model="form.id_sign_price"
                                type="number"
                                step="0.01"
                                min="0"
                                class="form-control"
                                placeholder="Use default"
                                :class="{ 'is-invalid': errors.id_sign_price }"
                            >
                        </div>
                        <small
                            v-if="originalIdSignPrice != null"
                            class="form-text text-muted"
                        >
                            Original: £{{ originalIdSignPrice | money }}
                        </small>
                        <error
                            :errors="errors"
                            name="id_sign_price"
                        />
                    </div>
                </div>
            </div>
            <div
                v-if="selectedVariations.length"
                class="mt-2"
            >
                <h6>Variation Prices</h6>
                <div class="table-responsive">
                    <table class="table table-sm">
                        <thead>
                            <tr>
                                <th>Variation</th>
                                <th>Basic Service</th>
                                <th>Refill/Recharge</th>
                                <th>Extended Service</th>
                                <th>Recon</th>
                                <th>New Price</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(variation, idx) in form.variations"
                                :key="variation.service_product_variation_id"
                            >
                                <td class="align-middle">
                                    {{ variation.name }}
                                </td>
                                <td>
                                    <div class="input-group input-group-sm">
                                        <div class="input-group-prepend">
                                            <span class="input-group-text">£</span>
                                        </div>
                                        <input
                                            v-model="variation.bs_price"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            class="form-control"
                                            :placeholder="variation.original_bs_price != null ? variation.original_bs_price : '—'"
                                        >
                                    </div>
                                </td>
                                <td>
                                    <div class="input-group input-group-sm">
                                        <div class="input-group-prepend">
                                            <span class="input-group-text">£</span>
                                        </div>
                                        <input
                                            v-model="variation.rc_price"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            class="form-control"
                                            :placeholder="variation.rc_price_original != null ? variation.rc_price_original : '—'"
                                        >
                                    </div>
                                </td>
                                <td>
                                    <div class="input-group input-group-sm">
                                        <div class="input-group-prepend">
                                            <span class="input-group-text">£</span>
                                        </div>
                                        <input
                                            v-model="variation.es_price"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            class="form-control"
                                            :placeholder="variation.es_price_original != null ? variation.es_price_original : '—'"
                                        >
                                    </div>
                                </td>
                                <td>
                                    <div class="input-group input-group-sm">
                                        <div class="input-group-prepend">
                                            <span class="input-group-text">£</span>
                                        </div>
                                        <input
                                            v-model="variation.rcon_price"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            class="form-control"
                                            :placeholder="variation.rcon_price_original != null ? variation.rcon_price_original : '—'"
                                        >
                                    </div>
                                </td>
                                <td>
                                    <div class="input-group input-group-sm">
                                        <div class="input-group-prepend">
                                            <span class="input-group-text">£</span>
                                        </div>
                                        <input
                                            v-model="variation.new_price"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            class="form-control"
                                            :placeholder="variation.new_price_original != null ? variation.new_price_original : '—'"
                                        >
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <small class="form-text text-muted">
                    Leave fields empty to use the default price. Placeholders show the original price.
                </small>
            </div>
        </form>
        <template slot="footer">
            <div class="d-flex w-100 justify-content-between">
                <div>
                    <button
                        v-if="form.editingId"
                        type="button"
                        class="btn btn-outline-danger"
                        :disabled="saving"
                        @click="confirmDelete"
                    >
                        <i class="fa fa-trash" /> Delete
                    </button>
                </div>
                <div>
                    <button
                        type="button"
                        class="btn btn-secondary"
                        @click="$refs.modal.close()"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        :disabled="saving"
                        @click="save"
                    >
                        {{ form.editingId ? 'Save' : 'Add' }}
                    </button>
                </div>
            </div>
        </template>
    </modal>
</template>
<script>
import CustomerServiceProductService from '../../../services/CustomerServiceProductService';
import UsesErrors from '../../../mixins/UsesErrors';
import UsesInfoMessage from '../../../mixins/UsesInfoMessage';
import Modal from '../../modals/ModalComponent.vue';

export default {
    components: {
        Modal,
    },
    mixins: [UsesErrors, UsesInfoMessage],
    props: {
        customerId: {
            type: [String, Number],
            required: true,
        },
        serviceProducts: {
            type: Array,
            default: function () { return []; },
        },
        customerServiceProducts: {
            type: Array,
            default: function () { return []; },
        },
        editingProduct: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            form: {
                editingId: null,
                service_product_id: null,
                productDisplayName: '',
                fitting_price: '',
                id_sign_price: '',
                variations: [],
            },
            saving: false,
        };
    },
    computed: {
        modalTitle() {
            return this.form.editingId ? 'Edit Service Product' : 'Add Service Product';
        },
        selectedServiceProduct() {
            if (!this.form.service_product_id) return null;
            return this.serviceProducts.find(function (sp) {
                return sp.id == this.form.service_product_id;
            }.bind(this));
        },
        selectedVariations() {
            if (!this.selectedServiceProduct || !this.selectedServiceProduct.variations) return [];
            return this.selectedServiceProduct.variations;
        },
        originalFittingPrice() {
            if (!this.selectedServiceProduct) return null;
            return this.selectedServiceProduct.fitting_price;
        },
        originalIdSignPrice() {
            if (!this.selectedServiceProduct) return null;
            return this.selectedServiceProduct.id_sign_price;
        },
        availableServiceProducts() {
            var usedIds = this.customerServiceProducts
                .filter(function (csp) {
                    return !this.form.editingId || csp.id !== this.form.editingId;
                }.bind(this))
                .map(function (csp) { return csp.service_product_id; });
            return this.serviceProducts.filter(function (sp) {
                return usedIds.indexOf(sp.id) === -1;
            });
        },
    },
    mounted() {
        if (this.editingProduct) {
            var sp = this.editingProduct.service_product;
            this.form = {
                editingId: this.editingProduct.id,
                service_product_id: this.editingProduct.service_product_id,
                productDisplayName: sp ? sp.name : '',
                fitting_price: this.editingProduct.fitting_price != null ? this.editingProduct.fitting_price : '',
                id_sign_price: this.editingProduct.id_sign_price != null ? this.editingProduct.id_sign_price : '',
                variations: this.buildVariationsForm(sp, this.editingProduct.variations),
            };
        }
        this.$refs.modal.open();
    },
    methods: {
        buildVariationsForm(serviceProduct, existingOverrides) {
            if (!serviceProduct || !serviceProduct.variations) return [];
            var overrides = existingOverrides || [];
            return serviceProduct.variations.map(function (v) {
                var override = overrides.find(function (o) {
                    return o.service_product_variation_id === v.id;
                });
                return {
                    service_product_variation_id: v.id,
                    name: v.name,
                    bs_price: override && override.bs_price != null ? override.bs_price : '',
                    rc_price: override && override.rc_price != null ? override.rc_price : '',
                    es_price: override && override.es_price != null ? override.es_price : '',
                    rcon_price: override && override.rcon_price != null ? override.rcon_price : '',
                    new_price: override && override.new_price != null ? override.new_price : '',
                    original_bs_price: v.bs_price,
                    rc_price_original: v.rc_price,
                    es_price_original: v.es_price,
                    rcon_price_original: v.rcon_price,
                    new_price_original: v.new_price,
                };
            });
        },
        onProductChange() {
            if (this.selectedServiceProduct) {
                this.form.variations = this.buildVariationsForm(this.selectedServiceProduct, []);
            } else {
                this.form.variations = [];
            }
        },
        resetForm() {
            this.form = {
                editingId: null,
                service_product_id: null,
                productDisplayName: '',
                fitting_price: '',
                id_sign_price: '',
                variations: [],
            };
        },
        handleClose() {
            this.$emit('close');
        },
        confirmDelete() {
            if (!window.confirm('Are you sure you want to remove this service product from the customer?')) {
                return;
            }
            this.saving = true;
            CustomerServiceProductService.destroy(this.customerId, this.form.editingId).then(function () {
                this.$refs.modal && this.$refs.modal.close();
                this.resetForm();
                this.$emit('deleted');
                this.setInfoMessage('Service product removed successfully');
            }.bind(this)).catch(function (err) {
                if (err.response && err.response.data && err.response.data.errors) {
                    this.captureRequestErrors(err);
                } else {
                    this.showErrorMessage('Failed to remove service product');
                }
            }.bind(this)).then(function () {
                this.saving = false;
            }.bind(this));
        },
        save() {
            var validationErrors = {};
            if (!this.form.editingId && !this.form.service_product_id) {
                validationErrors.service_product_id = ['Please select a service product'];
            }
            if (Object.keys(validationErrors).length) {
                this.setErrors(validationErrors);
                return;
            }
            this.clearErrors();
            this.saving = true;

            var variationsData = this.form.variations
                .filter(function (v) {
                    return v.bs_price !== '' || v.rc_price !== '' || v.es_price !== '' || v.rcon_price !== '' || v.new_price !== '';
                })
                .map(function (v) {
                    return {
                        service_product_variation_id: v.service_product_variation_id,
                        bs_price: v.bs_price !== '' ? v.bs_price : null,
                        rc_price: v.rc_price !== '' ? v.rc_price : null,
                        es_price: v.es_price !== '' ? v.es_price : null,
                        rcon_price: v.rcon_price !== '' ? v.rcon_price : null,
                        new_price: v.new_price !== '' ? v.new_price : null,
                    };
                });

            var payload = {
                fitting_price: this.form.fitting_price !== '' ? this.form.fitting_price : null,
                id_sign_price: this.form.id_sign_price !== '' ? this.form.id_sign_price : null,
                variations: variationsData,
            };

            var promise = this.form.editingId
                ? CustomerServiceProductService.update(this.customerId, this.form.editingId, payload)
                : CustomerServiceProductService.create(this.customerId, Object.assign({
                    service_product_id: this.form.service_product_id,
                }, payload));

            var wasEditing = !!this.form.editingId;
            promise.then(function () {
                this.$refs.modal && this.$refs.modal.close();
                this.resetForm();
                this.$emit('saved', { wasEditing: wasEditing });
                this.setInfoMessage(wasEditing ? 'Service product updated successfully' : 'Service product added successfully');
            }.bind(this)).catch(function (err) {
                this.captureRequestErrors(err);
            }.bind(this)).then(function () {
                this.saving = false;
            }.bind(this));
        },
    },
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/office/customers/CustomerServiceProductModal.vue